import Link from 'components/ui/AppLink';
import Loader from 'components/ui/LoadingIndicator';
import UserInterfaceCheckSmall from '@mds/icons/Check';
import { useQuery } from '@apollo/react-hooks';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Grid,
    Paper,
    Snackbar,
    Typography,
    Typography as BreadcrumbsText,
} from '@mui/material';
import { MessageView } from 'components/ui/Feedback';
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { GET_PREMIUM_CHANNEL_BY_ID_LEAN } from '../../../gql/channels';
import { Channel, ChannelSubscription } from '../../../shared/types';
import { baseUrls } from '../../../utils/baseUrls.utils';
import {
    getChannelSubscriptionConfirmationLink,
    getChannelSubscriptionInterestConfirmLink,
} from '../../../utils/link.utils';
import { EmailNeonSales } from '../../ui/EmailNeonSales';
import SectionHeader from '../../ui/SectionHeader';
import { GET_USER_PREMIUM_CHANNEL_SUBSCRIPTIONS } from '../../../gql/users';
import { useRequestFreeTrial } from '../../hooks/use-request-free-trial';
import { isValidMongoId } from '../../../utils/id-validation.utils';

export const PremiumChannelSubscriptionScreen = () => {
    const { channelId } = useParams<{ channelId: string }>();
    const isValidChannelId = isValidMongoId(channelId);
    const history = useHistory();

    const { data, loading } = useQuery<{ channel: Channel }>(
        GET_PREMIUM_CHANNEL_BY_ID_LEAN,
        {
            skip: !isValidChannelId,
            variables: { id: channelId },
            fetchPolicy: 'cache-first',
        }
    );
    const { data: subscriptions, loading: loadingSubscriptions } = useQuery(
        GET_USER_PREMIUM_CHANNEL_SUBSCRIPTIONS,
        {
            skip: !isValidChannelId,
            fetchPolicy: 'network-only',
        }
    );
    const [requestTrial, loadingTrialRequest] = useRequestFreeTrial();
    const [trialRequestErrorVisible, setTrialRequestErrorVisible] =
        useState(false);

    const currentSubscription =
        subscriptions?.premiumChannelSubscriptions?.find(
            (pc: ChannelSubscription) => pc.channelId === channelId
        );
    const existingSubscription = !!currentSubscription;

    const requestTrialHandler = () =>
        requestTrial(channelId)
            .then(() =>
                history.push(
                    getChannelSubscriptionInterestConfirmLink(channelId) +
                        '?trial'
                )
            )
            .catch(() => setTrialRequestErrorVisible(true));

    const buyPremiumChannelAccessHandler = () => {
        history.push(getChannelSubscriptionConfirmationLink(channelId));
    };

    if (!isValidChannelId) {
        return (
            <MessageView
                styleName="primary"
                size="large"
                title="Channel not found"
                primaryAction={{
                    label: 'Return to Neon Insights',
                    onClick: () => history.push('/'),
                }}
            />
        );
    }

    if (loading || loadingSubscriptions || loadingTrialRequest) {
        return <Loader />;
    }

    return (
        <>
            <Snackbar
                open={trialRequestErrorVisible}
                onClose={() => setTrialRequestErrorVisible(false)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="error"
                    onClose={() => setTrialRequestErrorVisible(false)}
                >
                    We could not request your free trial. Please try again.
                </Alert>
            </Snackbar>
            <Grid container spacing={4}>
                <Grid size={12}>
                    <Breadcrumbs>
                        <Link to={baseUrls.channels.main}>All channels</Link>
                        <Link
                            to={`${baseUrls.browse.channelMain}/${data?.channel._id}`}
                        >
                            {data?.channel.name}
                        </Link>
                        <BreadcrumbsText>Subscribe</BreadcrumbsText>
                    </Breadcrumbs>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        md: 8,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <SectionHeader
                                id="premium-channel-subscription-confirmation-header"
                                title={`Subscribe to ${data?.channel.name}`}
                                subtitle={data?.channel.description}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Grid container spacing={1}>
                                <Grid size={12}>
                                    <Typography variant="body1">
                                        ${data?.channel.price}/month*
                                    </Typography>
                                </Grid>
                                <Grid size={12}>
                                    <Typography
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        *Price excludes applicable sales taxes
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={12}>
                            <Grid container spacing={1}>
                                <Grid size={12}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            '&>svg': { mr: 1 },
                                        }}
                                    >
                                        <UserInterfaceCheckSmall />{' '}
                                        <Typography>
                                            Billed monthly, subscription renews
                                            until cancelled
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            '&>svg': { mr: 1 },
                                        }}
                                    >
                                        <UserInterfaceCheckSmall />{' '}
                                        <Typography>
                                            Access on any device
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            '&>svg': { mr: 1 },
                                        }}
                                    >
                                        <UserInterfaceCheckSmall />{' '}
                                        <Typography>Cancel anytime</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={12}>
                            <Button
                                size="medium"
                                variant="contained"
                                onClick={buyPremiumChannelAccessHandler}
                            >
                                Buy Premium access
                            </Button>
                        </Grid>
                        {!loadingSubscriptions && !existingSubscription && (
                            <>
                                <Grid size={12}>
                                    <Grid container spacing={1}>
                                        <Grid size={12}>
                                            <Typography
                                                variant="body1"
                                                sx={{ mt: { xs: 2, md: 6 } }}
                                            >
                                                Alternatively, you can request a
                                                free trial
                                            </Typography>
                                        </Grid>
                                        <Grid size={12}>
                                            <Typography
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                Get started with Channels for
                                                free and gain access to market
                                                leading commentary from industry
                                                experts.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid size={12}>
                                    <Button
                                        size="medium"
                                        variant="contained"
                                        onClick={requestTrialHandler}
                                        disabled={loadingTrialRequest}
                                    >
                                        Request FREE trial now
                                    </Button>
                                </Grid>
                                <Grid size={12}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Once approved, you’ll have{' '}
                                        <strong>
                                            full access for{' '}
                                            {
                                                data?.channel
                                                    .defaultTrialDurationDays
                                            }{' '}
                                            days.
                                        </strong>
                                    </Typography>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        md: 4,
                    }}
                >
                    <Paper>
                        <Box
                            sx={{
                                p: 4,
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <Typography variant="h6">
                                        If you need further assistance
                                    </Typography>
                                </Grid>
                                <Grid size={12}>
                                    <Typography>
                                        <strong>Email us at</strong>
                                    </Typography>
                                    <Typography>
                                        <EmailNeonSales />
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </>
    );
};
